/**
 * Aegae's own backend: the protocol handshake and the session.
 *
 * It is a Pages Function on **this same origin** (`functions/api/[[path]].ts`),
 * which is why every path below is relative. No origin to configure, no CORS
 * preflight, no second deploy that can drift from this one.
 *
 * Aegae used to ride on leeseidon's Worker, D1 and user accounts. It does not
 * any more, and that separation is the point: the two apps shared one
 * passphrase-derived vault salt per account, so rotating leeseidon's passphrase
 * silently orphaned every Aegae row written before it. There is no shared
 * anything now.
 *
 * INV-8 — **nothing in this file navigates.** Sign-in posts a Google Identity
 * Services credential to `/api/v1/auth/google/idtoken` and gets a session back
 * in the response body. A redirect-based flow is prohibited: on iOS an installed
 * PWA and Safari have separate storage jars, so any navigation out of scope can
 * land the session somewhere this app will never read it.
 */

export class SessionExpiredError extends Error {
  constructor() {
    super("Your session has expired. Sign in again to resume syncing.");
    this.name = "SessionExpiredError";
  }
}

import { signOutPending } from "./sign_out_marker";
import { API_BASE } from "./api_base";

/**
 * The wire protocol version this client speaks.
 *
 * The backend advertises its own at `GET /api/v1/protocol`, and a mismatch fails
 * closed. Client and backend now deploy together, but a PWA is a *cached*
 * client: an install that has not updated can be weeks behind the server it is
 * talking to. Raise this only alongside a matching `server/protocol.ts` change.
 */
export const CLIENT_PROTOCOL_VERSION = 4;

let updateRequired = false;
const updateListeners = new Set<() => void>();
/** Reset protocol state. Test-only. */
export function resetProtocolStateForTest(): void { updateRequired = false; }

export class SyncHttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "SyncHttpError";
  }
}

export function protocolUpdateRequired(): boolean { return updateRequired; }
export function subscribeProtocolUpdate(listener: () => void): () => void {
  updateListeners.add(listener);
  return () => { updateListeners.delete(listener); };
}

export class ProtocolMismatchError extends Error {
  constructor(serverVersion: number, minClient: number) {
    super(
      `This version of Tide Tally speaks protocol ${String(CLIENT_PROTOCOL_VERSION)}, ` +
        `but the server speaks ${String(serverVersion)} (minimum ${String(minClient)}). ` +
        `Update the app before syncing — your ledger is safe in your account, and anything newer is saved on this device until it uploads.`,
    );
    this.name = "ProtocolMismatchError";
  }
}

/**
 * Confirm we understand the server before writing anything to it.
 *
 * **Fails closed.** A client that pushes rows in a format the server no longer
 * understands corrupts its own account. Refusing to sync and saying so is always
 * the better outcome — the data is still on the device either way.
 */
export async function assertProtocolCompatible(fetchImpl: typeof fetch = fetch): Promise<void> {
  const res = await fetchImpl(`${API_BASE}/v1/protocol`);
  if (!res.ok) {
    throw new SyncHttpError(`Could not reach the sync service (HTTP ${String(res.status)})`, res.status);
  }
  const body = (await res.json()) as {
    protocol_version?: unknown;
    min_client_protocol?: unknown;
  };

  const serverVersion = body.protocol_version;
  const minClient = body.min_client_protocol;
  if (typeof serverVersion !== "number" || typeof minClient !== "number") {
    throw new Error("The sync service returned an unrecognisable protocol response");
  }
  if (CLIENT_PROTOCOL_VERSION < minClient || CLIENT_PROTOCOL_VERSION > serverVersion) {
    updateRequired = true;
    updateListeners.forEach((listener) => listener());
    throw new ProtocolMismatchError(serverVersion, minClient);
  }
}

/* ── session ───────────────────────────────────────────────────────────────── */

export interface Session {
  token: string;
  accountId: string;
  email: string;
  expiresAt: string;
}

const SESSION_KEY = "aegae.session.v1";
export const PENDING_NATIVE_SESSION_KEY = "aegae.nativeSignInPending.v1";

/**
 * A second copy of the session outside the WebView — the Keychain on iOS, a
 * Keystore-wrapped value on Android (`TallyNative`). Two reasons: a WebView can
 * lose its localStorage (iOS reclaims it under storage pressure), which must
 * not sign anyone out; and the widgets and App Intents upload with the same
 * token. Null on the web, where there is nothing outside the browser to copy to.
 *
 * Every write here is mirrored, and a mirror failure is reported, never
 * thrown: localStorage is still the session of record for the app itself.
 */
export interface SessionMirror {
  get(): Promise<Session | null>;
  set(session: Session): Promise<void>;
  clear(): Promise<void>;
}

let mirror: SessionMirror | null = null;

export function setSessionMirror(next: SessionMirror | null): void {
  mirror = next;
}

function mirrorFailed(what: string): (cause: unknown) => void {
  return (cause) => {
    console.warn(
      `Could not ${what} the session's native copy:`,
      cause instanceof Error ? cause.message : cause,
    );
  };
}

/**
 * Persisted in `localStorage`, deliberately — not `sessionStorage`.
 *
 * Aegae is an installed app opened dozens of times a day; a token that died on
 * close would mean signing in every single time, which is the one thing this
 * product cannot do. The install gate is what keeps it durable (iOS evicts a
 * *browser tab's* storage after 7 idle days; installed apps are exempt).
 *
 * An opaque token is all that is stored — there is no key, and nothing here is
 * of any use on another device.
 */
export function readSession(): Session | null {
  try {
    if (signOutPending()) return null;
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.accountId !== "string" ||
      typeof parsed.email !== "string" ||
      typeof parsed.expiresAt !== "string"
    ) {
      // A malformed session is a signed-out state, not something to patch up.
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    // An expired session is a signed-out state. Keeping it would leave the app
    // reporting "signed in" while every request 401s, and the sync error would
    // promise the queue "will upload automatically" — which it never would.
    if (Date.parse(parsed.expiresAt) <= Date.now()) {
      clearSession();
      return null;
    }
    return parsed as Session;
  } catch {
    // Storage blocked or unreadable. Treated as signed out, which is the safe
    // direction: the alternative is acting on a session we cannot verify.
    return null;
  }
}

/** Sign-in publishes only after the native marker is replaced successfully. */
export async function writeSessionDurably(session: Session): Promise<void> {
  if (mirror) localStorage.setItem(PENDING_NATIVE_SESSION_KEY, JSON.stringify(session));
  await mirror?.set(session);
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.removeItem(PENDING_NATIVE_SESSION_KEY);
}

/** Retry a failed sign-in handoff before a surviving native sign-out marker can erase its ledger. */
export async function recoverPendingSessionWrite(): Promise<void> {
  const raw = localStorage.getItem(PENDING_NATIVE_SESSION_KEY);
  if (raw === null) return;
  const value: unknown = JSON.parse(raw);
  if (typeof value !== "object" || value === null || !("token" in value) || !("accountId" in value) || !("email" in value) || !("expiresAt" in value) ||
      typeof value.token !== "string" || typeof value.accountId !== "string" || typeof value.email !== "string" || typeof value.expiresAt !== "string") throw new Error("Pending native sign-in is malformed; cleanup is blocked to protect this ledger.");
  if (!(Date.parse(value.expiresAt) > Date.now())) { localStorage.removeItem(PENDING_NATIVE_SESSION_KEY); return; }
  await writeSessionDurably({ token: value.token, accountId: value.accountId, email: value.email, expiresAt: value.expiresAt });
}

export function writeSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  mirror?.set(session).catch(mirrorFailed("write"));
}

/**
 * At boot, before anything renders: if the WebView has lost the session but
 * its native copy survives, put it back. Returns true if it restored one.
 * An expired or malformed copy is cleared instead — the same rule as
 * `readSession`.
 */
export async function restoreSessionFromMirror(): Promise<boolean> {
  if (signOutPending() || mirror === null) return false;
  const saved = await mirror.get();
  // Native uploads slide the session too. Adopt that newer expiry before readSession can discard
  // an older WebView copy and clear a still-live mirror.
  if (saved !== null && Date.parse(saved.expiresAt) > Date.now()) {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored !== null) {
      try {
        const previous = JSON.parse(stored) as Partial<Session>;
        if (previous.token === saved.token && typeof previous.expiresAt === "string" &&
            Date.parse(previous.expiresAt) < Date.parse(saved.expiresAt)) {
          localStorage.setItem(SESSION_KEY, JSON.stringify(saved));
        }
      } catch (cause) {
        console.warn("Could not read the WebView session while restoring its native copy:", cause);
      }
    }
  }
  const local = readSession();
  // A background uploader can learn of a revocation while the WebView is closed.
  if (local !== null) {
    if (saved?.token === local.token && Date.parse(saved.expiresAt) <= Date.now()) clearSession();
    return false;
  }
  if (saved === null) return false;
  if (Date.parse(saved.expiresAt) <= Date.now()) {
    await mirror.clear();
    return false;
  }
  // Straight to storage: re-mirroring what was just read from the mirror
  // would be a pointless round-trip.
  localStorage.setItem(SESSION_KEY, JSON.stringify(saved));
  return true;
}

/** The header the server uses to report a slid session expiry. */
export const SESSION_EXPIRES_HEADER = "x-session-expires-at";

/**
 * Adopt a session expiry the server just extended.
 *
 * Sessions slide: every authenticated request pushes the server's `expires_at`
 * forward, so an app opened daily never expires out from under its user. But
 * `readSession()` discards the session the moment the **locally stored** expiry
 * passes — so without copying the new value down, the slide would be invisible
 * here and a daily user would still be signed out on day 31, against a server
 * that considered them signed in for another thirty.
 *
 * A no-op when the header is absent or unparseable, and it never moves an expiry
 * *backwards*: a stale or malformed value must not shorten a good session.
 */
export function adoptSessionExpiry(response: Response): void {
  const header = response.headers.get(SESSION_EXPIRES_HEADER);
  if (header === null) return;

  const next = Date.parse(header);
  if (!Number.isFinite(next)) return;

  const current = readSession();
  if (current === null || next <= Date.parse(current.expiresAt)) return;

  writeSession({ ...current, expiresAt: header });
}

export function clearSession(): void {
  localStorage.removeItem(PENDING_NATIVE_SESSION_KEY);
  localStorage.removeItem(SESSION_KEY);
  mirror?.clear().catch(mirrorFailed("clear"));
}

/**
 * Exchange a Google Identity Services credential for an Aegae session.
 *
 * The account is Aegae's own, keyed on the Google subject. Signing in with the
 * same Google account you use for leeseidon no longer lands you on a shared
 * `account_id` — the two apps are separate systems now, with separate data.
 */
export async function signInWithGoogleCredential(
  credential: string,
  rawNonce: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Session> {
  const res = await fetchImpl(`${API_BASE}/v1/auth/google/idtoken`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id_token: credential, nonce: rawNonce }),
  });

  if (!res.ok) {
    // The backend deliberately returns one opaque code for every verification
    // failure, so there is nothing more specific to surface here.
    throw new Error("Google sign-in was refused. Please try again.");
  }

  const body = (await res.json()) as Record<string, unknown>;
  if (
    typeof body.session_token !== "string" ||
    typeof body.account_id !== "string" ||
    typeof body.email !== "string" ||
    typeof body.expires_at !== "string"
  ) {
    throw new Error("Sign-in succeeded but the response was unrecognisable");
  }

  return {
    token: body.session_token,
    accountId: body.account_id,
    email: body.email,
    expiresAt: body.expires_at,
  };
}

/**
 * Exchange a Sign in with Apple identity token for an Aegae session (iOS only).
 *
 * `created` is true when this Apple identity has just made a new account — the
 * app then says so once, because someone who used Google before has just got a
 * second, empty account (SPEC §11.1). Accounts are never matched by email.
 */
export async function signInWithAppleIdToken(
  idToken: string,
  rawNonce: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ session: Session; created: boolean }> {
  const res = await fetchImpl(`${API_BASE}/v1/auth/apple/idtoken`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id_token: idToken, nonce: rawNonce }),
  });
  if (!res.ok) throw new Error("Apple sign-in was refused. Please try again.");

  const body = (await res.json()) as Record<string, unknown>;
  if (
    typeof body.session_token !== "string" ||
    typeof body.account_id !== "string" ||
    typeof body.email !== "string" ||
    typeof body.expires_at !== "string"
  ) {
    throw new Error("Sign-in succeeded but the response was unrecognisable");
  }
  return {
    session: {
      token: body.session_token,
      accountId: body.account_id,
      email: body.email,
      expiresAt: body.expires_at,
    },
    created: body.created === true,
  };
}

export async function signOut(token: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  try {
    await fetchImpl(`${API_BASE}/v1/auth/logout`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}` },
    });
  } finally {
    // Local state is cleared even if the network call fails. A device that still
    // holds a session after the user pressed sign out is worse than a stale row
    // on the server.
    clearSession();
  }
}

/* ── identities ────────────────────────────────────────────────────────────── */

/** Which sign-in identities this account has. Booleans only: subs never leave the server. */
export interface LinkedIdentities {
  google: boolean;
  apple: boolean;
  /** A label, possibly an Apple private-relay address, possibly empty. Never an identity. */
  email: string;
}

export type IdentityProvider = "google" | "apple";

const PROVIDER_NAMES: Record<IdentityProvider, string> = { google: "Google", apple: "Apple" };

function parseIdentities(body: unknown): LinkedIdentities {
  const record = (body ?? {}) as Record<string, unknown>;
  if (
    typeof record.google !== "boolean" ||
    typeof record.apple !== "boolean" ||
    typeof record.email !== "string"
  ) {
    throw new Error("The server returned an unrecognisable list of sign-in identities");
  }
  return { google: record.google, apple: record.apple, email: record.email };
}

/** `GET /auth/identities` for the session's own account. */
export async function fetchIdentities(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LinkedIdentities> {
  // A failed request rejects with the browser's wording ("Load failed",
  // "Failed to fetch"); the screen shows a sentence, the original rides as cause.
  const res = await fetchImpl(`${API_BASE}/v1/auth/identities`, {
    method: "GET",
    headers: { authorization: `Bearer ${token}` },
  }).catch((cause: unknown) => {
    throw new Error("Could not reach your account. Check your connection and try again.", { cause });
  });
  adoptSessionExpiry(res);
  if (res.status === 401) throw new Error("Your session has expired. Sign in again to see this.");
  if (!res.ok) throw new Error(`Could not read your sign-in methods (HTTP ${String(res.status)})`);
  return parseIdentities(await res.json());
}

/**
 * Link a second identity to the session's account with a freshly verified ID
 * token. **Never a merge**: an identity that already has its own account is
 * refused, and the message says so plainly rather than offering to combine them.
 */
export async function linkIdentity(
  provider: IdentityProvider,
  token: string,
  idToken: string,
  rawNonce: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LinkedIdentities> {
  const name = PROVIDER_NAMES[provider];
  const res = await fetchImpl(`${API_BASE}/v1/auth/link/${provider}`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ id_token: idToken, nonce: rawNonce }),
  });
  adoptSessionExpiry(res);
  if (res.ok) return parseIdentities(await res.json());

  if (res.status === 409) {
    const body = (await res.json().catch((cause: unknown) => {
      // Reported, and the status alone still says enough to answer (INV-7).
      console.warn("Unreadable 409 from the link endpoint:", cause);
      return {};
    })) as { error?: unknown };
    if (body.error === "already_linked") {
      throw new Error(`This account already has a ${name} sign-in.`);
    }
    throw new Error(
      `That ${name} account already has its own Tide Tally account. Accounts are never merged, so it cannot be linked here.`,
    );
  }
  if (res.status === 401) {
    throw new Error(`${name} did not confirm that sign-in, or your session has ended. Try again.`);
  }
  throw new Error(`Could not link ${name} (HTTP ${String(res.status)})`);
}


/** Family responses contain member ids, never other accounts' ids. */
export interface FamilyMember {
  member_id: string;
  display_name: string;
  role: "owner" | "member";
  joined_at: string;
  left_at: string | null;
}
export interface FamilyView {
  family_id: string;
  name: string;
  created_at: string;
  my_member_id: string;
  members: FamilyMember[];
  invites: { invite_id: string; created_by_member: string; created_at: string; expires_at: string }[];
}
export type FamilyCredential = { code: string; token?: never } | { token: string; code?: never };
export interface FamilyInvite { invite_id: string; code: string; token: string; expires_at: string }
export interface FamilyPreview { name: string; inviter_display_name: string; member_count: number }
export class FamilyHttpError extends SyncHttpError {
  readonly code: string;
  constructor(status: number, code: string) { super(`Family request failed (${code}, HTTP ${String(status)}). Local changes are retained.`, status); this.code = code; }
}
const FAMILY_REQUEST_TIMEOUT_MS = 10_000;
async function familyRequest<T>(path: string, method: string, body?: object, fetchImpl: typeof fetch = fetch): Promise<T> {
  const session = readSession();
  if (!session) throw new Error("Sign in before managing a family.");
  const res = await fetchImpl(`${API_BASE}/v1/family${path}`, { method, signal: AbortSignal.timeout(FAMILY_REQUEST_TIMEOUT_MS),
    headers: { authorization: `Bearer ${session.token}`, "content-type": "application/json", "x-tally-protocol": String(CLIENT_PROTOCOL_VERSION) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  adoptSessionExpiry(res);
  if (res.status === 401) throw new SessionExpiredError();
  if (!res.ok) {
    const data: unknown = await res.json().catch(() => null);
    const code = typeof data === "object" && data !== null && "error" in data && typeof data.error === "string" ? data.error : "unrecognisable_response";
    throw new FamilyHttpError(res.status, code);
  }
  const data: unknown = await res.json();
  validateFamilyResponse(data, path, method);
  return data as T;
}
function familyRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("Unrecognisable family response.");
  return value as Record<string, unknown>;
}
function familyStrings(value: Record<string, unknown>, keys: string[]): void {
  if (keys.some(key => typeof value[key] !== "string")) throw new Error("Unrecognisable family response fields.");
}
export function validateFamilyResponse(data: unknown, path: string, method: string): void {
  const body = familyRecord(data);
  if (path === "/invites" && method === "POST") { familyStrings(body, ["invite_id", "code", "token", "expires_at"]); return; }
  if (path === "/join/preview") {
    familyStrings(body, ["name", "inviter_display_name"]);
    if (!Number.isInteger(body.member_count) || Number(body.member_count) < 0) throw new Error("Unrecognisable family member count.");
    return;
  }
  if (body.family === null) return;
  const family = familyRecord(body.family);
  familyStrings(family, ["family_id", "name", "created_at", "my_member_id"]);
  if (!family.family_id || !Array.isArray(family.members) || !Array.isArray(family.invites)) throw new Error("Unrecognisable family membership response.");
  for (const value of family.members as unknown[]) {
    const member = familyRecord(value);
    familyStrings(member, ["member_id", "display_name", "joined_at"]);
    if ((member.role !== "member" && member.role !== "owner") || (member.left_at !== null && typeof member.left_at !== "string")) throw new Error("Unrecognisable family member.");
  }
  for (const value of family.invites as unknown[]) familyStrings(familyRecord(value), ["invite_id", "created_by_member", "created_at", "expires_at"]);
}
type FamilyResponse = { family: FamilyView | null };
export async function getFamily(fetchImpl: typeof fetch = fetch): Promise<FamilyView | null> {
  const response = await familyRequest<FamilyResponse>("", "GET", undefined, fetchImpl);
  if (response.family === null) return null;
  return response.family;
}
export function createFamily(name: string, displayName: string): Promise<FamilyResponse> { return familyRequest("", "POST", { name, display_name: displayName }); }
export function renameFamily(name: string): Promise<FamilyResponse> { return familyRequest("", "PATCH", { name }); }
export function updateFamilyMe(displayName: string): Promise<FamilyResponse> { return familyRequest("/me", "PATCH", { display_name: displayName }); }
export function inviteFamily(): Promise<FamilyInvite> { return familyRequest("/invites", "POST"); }
export function revokeFamilyInvite(inviteId: string): Promise<FamilyResponse> { return familyRequest(`/invites/${encodeURIComponent(inviteId)}`, "DELETE"); }
export function previewFamily(credential: FamilyCredential): Promise<FamilyPreview> { return familyRequest("/join/preview", "POST", credential); }
export function joinFamily(credential: FamilyCredential, displayName: string): Promise<FamilyResponse> { return familyRequest("/join", "POST", { ...credential, display_name: displayName }); }
export function leaveFamilyMembership(confirmDelete = false): Promise<FamilyResponse> { return familyRequest("/leave", "POST", { confirm_delete: confirmDelete }); }
export function removeFamilyMember(memberId: string): Promise<FamilyResponse> { return familyRequest(`/members/${encodeURIComponent(memberId)}/remove`, "POST"); }
export function transferFamilyOwner(memberId: string): Promise<FamilyResponse> { return familyRequest("/owner", "POST", { member_id: memberId }); }

export interface LinkedSplit { link_id: string; member_id: string; owner_label: string; group_name: string; ended: boolean }
export interface OwnerSplitLink { link_id: string; member_id: string; linked_label: string; created_at: string; ended: boolean }
export interface SplitInviteMeta { invite_id: string; member_id: string; expires_at: string }
export interface SplitPreview { owner_label: string; group_name: string; member_name: string }
export interface LinkedPullRow { table: string; row_id: string; seq: number; updated_at: string; deleted: boolean; payload: string }
export interface LinkedPull { rows: LinkedPullRow[]; next_since: number; has_more: boolean }
export class SplitHttpError extends SyncHttpError {
  readonly code: string;
  constructor(status: number, code: string, reason: string) { super(reason, status); this.code = code; }
}
async function splitRequest(path: string, method: string, body?: object, fetchImpl: typeof fetch = fetch): Promise<Record<string, unknown>> {
  const session = readSession();
  if (!session) throw new SessionExpiredError();
  const response = await fetchImpl(`${API_BASE}/v1/splits${path}`, { method, signal: AbortSignal.timeout(FAMILY_REQUEST_TIMEOUT_MS),
    headers: { authorization: `Bearer ${session.token}`, "content-type": "application/json", "x-tally-protocol": String(CLIENT_PROTOCOL_VERSION) },
    ...(body ? { body: JSON.stringify(body) } : {}) });
  adoptSessionExpiry(response);
  if (response.status === 401) throw new SessionExpiredError();
  const data = familyRecord(await response.json());
  if (!response.ok) throw new SplitHttpError(response.status, typeof data.error === "string" ? data.error : "invalid_response", typeof data.reason === "string" ? data.reason : "Could not manage this split. Check your connection and try again.");
  return data;
}
function splitList<T>(value: unknown, keys: string[], ended = false): T[] {
  if (!Array.isArray(value)) throw new Error("Could not read shared groups. Try syncing again.");
  return value.map(item => {
    const row = familyRecord(item); familyStrings(row, keys);
    if (ended && typeof row.ended !== "boolean") throw new Error("Could not read link status. Try syncing again.");
    return row as T;
  });
}
export async function getLinkedSplits(fetchImpl: typeof fetch = fetch): Promise<LinkedSplit[]> {
  return splitList((await splitRequest("/linked", "GET", undefined, fetchImpl)).groups, ["link_id", "member_id", "owner_label", "group_name"], true);
}
export async function getSplitLinks(groupId: string): Promise<{ links: OwnerSplitLink[]; invites: SplitInviteMeta[] }> {
  const data = await splitRequest(`/links?group_id=${encodeURIComponent(groupId)}`, "GET");
  return { links: splitList(data.links, ["link_id", "member_id", "linked_label", "created_at"], true), invites: splitList(data.invites, ["invite_id", "member_id", "expires_at"]) };
}
export async function createSplitInvite(groupId: string, memberId: string, ownerLabel: string): Promise<FamilyInvite> {
  const row = await splitRequest("/invites", "POST", { group_id: groupId, member_id: memberId, owner_label: ownerLabel });
  familyStrings(row, ["invite_id", "code", "token", "expires_at"]); return row as unknown as FamilyInvite;
}
export async function previewSplit(credential: FamilyCredential): Promise<SplitPreview> {
  const row = await splitRequest("/join/preview", "POST", credential);
  familyStrings(row, ["owner_label", "group_name", "member_name"]); return row as unknown as SplitPreview;
}
export async function joinSplit(credential: FamilyCredential, displayName: string): Promise<void> { await splitRequest("/join", "POST", { ...credential, display_name: displayName }); }
export async function revokeSplitInvite(id: string): Promise<void> { await splitRequest(`/invites/${encodeURIComponent(id)}`, "DELETE"); }
export async function unlinkSplit(id: string): Promise<void> { await splitRequest(`/links/${encodeURIComponent(id)}`, "DELETE"); }
export async function unlinkSplitMember(groupId: string, memberId: string): Promise<void> { await splitRequest(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}/link`, "DELETE"); }
export async function pullLinkedSplit(id: string, since: number, fetchImpl: typeof fetch = fetch): Promise<LinkedPull> {
  const data = await splitRequest(`/linked/${encodeURIComponent(id)}/pull?since=${String(since)}`, "GET", undefined, fetchImpl);
  if (!Array.isArray(data.rows) || !Number.isSafeInteger(data.next_since) || Number(data.next_since) < since || typeof data.has_more !== "boolean") throw new Error("Could not read shared changes. Try syncing again.");
  for (const item of data.rows) {
    const row = familyRecord(item); familyStrings(row, ["table", "row_id", "updated_at", "payload"]);
    if (!Number.isSafeInteger(row.seq) || typeof row.deleted !== "boolean") throw new Error("Could not read shared changes. Try syncing again.");
  }
  return data as unknown as LinkedPull;
}
